import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage, LANGS, LANG_LABELS } from "@/i18n/LanguageContext";
import { scrollToId } from "@/config";
import BookingModal from "@/components/BookingModal";

export default function Navbar() {
  const { lang, t } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  const links = [
    { to: `/${lang}/about`, label: t.nav.about, id: "about" },
    { hash: "patients", label: t.nav.patients, id: "patients" },
    { hash: "directions", label: t.nav.directions, id: "directions" },
    { hash: "approach", label: t.nav.approach, id: "approach" },
    { hash: "process", label: t.nav.process, id: "process" },
    { hash: "diagnostics", label: t.nav.diagnostics, id: "diagnostics" },
    { to: `/${lang}/blog`, label: t.nav.blog, id: "blog" },
    { hash: "faq", label: t.nav.faq, id: "faq" },
    { to: `/${lang}/contacts`, label: t.nav.contacts, id: "contacts" },
  ];

  const goAnchor = (hash) => {
    if (pathname !== `/${lang}`) {
      navigate(`/${lang}#${hash}`);
    } else {
      scrollToId(hash);
    }
    setOpen(false);
  };

  const switchLang = (l) => {
    const rest = pathname.replace(/^\/(cs|ru|en)/, "") || "";
    navigate(`/${l}${rest}`);
    setOpen(false);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-cream/90 backdrop-blur-md border-b border-line/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to={`/${lang}`} className="shrink-0" data-testid="nav-logo">
          <span className="font-serif text-lg font-semibold tracking-tight leading-none block">Tatiana Nakryzhskaya</span>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted2 hidden sm:block">MD, PhD, CFMP</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-5" data-testid="nav-links">
          {links.map((l) =>
            l.hash ? (
              <button key={l.id} onClick={() => goAnchor(l.hash)} data-testid={`nav-link-${l.id}`} className="text-[13px] font-medium text-ink/75 hover:text-terra transition-colors whitespace-nowrap">
                {l.label}
              </button>
            ) : (
              <Link key={l.id} to={l.to} data-testid={`nav-link-${l.id}`} className={`text-[13px] font-medium transition-colors hover:text-terra whitespace-nowrap ${pathname === l.to ? "text-terra" : "text-ink/75"}`}>
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center rounded-full border border-line bg-white/60 p-0.5" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => switchLang(l)}
                data-testid={`lang-switcher-${l}`}
                className={`relative px-2.5 py-1 text-xs font-mono tracking-widest rounded-full transition-colors ${lang === l ? "text-cream" : "text-ink/60 hover:text-ink"}`}
              >
                {lang === l && (
                  <motion.span layoutId="lang-pill" className="absolute inset-0 bg-sage rounded-full" transition={{ type: "spring", stiffness: 400, damping: 30 }} />
                )}
                <span className="relative z-10">{LANG_LABELS[l]}</span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setBookingOpen(true)}
            data-testid="booking-cta-nav"
            className="hidden md:inline-flex items-center rounded-full bg-sage px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-ink whitespace-nowrap"
          >
            {t.nav.book}
          </button>
          <button className="xl:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="xl:hidden border-t border-line bg-cream px-6 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          {links.map((l) =>
            l.hash ? (
              <button key={l.id} onClick={() => goAnchor(l.hash)} data-testid={`mobile-nav-${l.id}`} className="text-left text-base font-medium py-2">
                {l.label}
              </button>
            ) : (
              <Link key={l.id} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}`} className="text-base font-medium py-2">
                {l.label}
              </Link>
            )
          )}
          <button onClick={() => { setOpen(false); setBookingOpen(true); }} data-testid="booking-cta-mobile" className="mt-2 rounded-full bg-sage px-4 py-2.5 text-sm font-semibold text-white">
            {t.nav.book}
          </button>
        </motion.nav>
      )}
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
    </header>
  );
}
